import { authSelectors } from 'ducks/auth'
import { logBannerAdClick, logBannerAdImpression } from 'elements/ads/adAnalytics'
import React, { ReactNode, useCallback, useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useSelector } from 'react-redux'
import { getUserIdentifier } from 'storage/adUserIdentifier'

const getRandomString = () => Math.floor(Math.random() * 10000000)

const ALL_BANNERS = {
  overflow: 'hidden',
}

export const BANNER_STYLES: Record<string, { overflow: string; width: number; height: number }> = {
  triple_widescreen: { ...ALL_BANNERS, width: 278, height: 400 },
  leaderboard: { ...ALL_BANNERS, width: 728, height: 90 },
  mobileLeaderboard: { ...ALL_BANNERS, width: 325, height: 50 },
}

export const AD_EVENT_TYPES = {
  IMPRESSION: 1,
  CLICK: 2,
  CONVERSION: 3,
}

export type AdCreativeData = {
  id: number
  name?: string
  format: string
  content_type: string
  content_url: string
  click_url: string
  image_file_contents: string | null
  text_content: string | null
}

export type AdServerResponse = {
  campaign_id: number | null
  campaign_name: string | null
  creative: AdCreativeData | null
}

type AdComponentProps = {
  creative: AdCreativeData
  rounded?: boolean
  onImpression: () => void
  onClick: () => void
}

// Banner ad component - displays an image that links to click_url
const AdBanner: React.FC<AdComponentProps> = ({ creative, rounded, onImpression, onClick }) => {
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: (inView) => {
      if (inView) onImpression()
    },
  })

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    onClick()
    if (creative.click_url) {
      if (creative.click_url.startsWith('http')) {
        window.open(creative.click_url, '_blank')
      } else {
        window.location.href = creative.click_url
      }
    }
  }

  const imageUrl = creative.image_file_contents || creative.content_url
  if (!imageUrl) return null

  const style = BANNER_STYLES[creative.format]

  return (
    <div style={{ width: '100%', textAlign: 'center' }} ref={ref}>
      <a href={creative.click_url || '#'} onClick={handleClick} style={{ display: 'inline-block', cursor: 'pointer' }}>
        <img
          src={imageUrl}
          alt="Advertisement"
          style={{
            ...style,
            border: 0,
            borderRadius: rounded ? 8 : undefined,
            display: 'block',
            objectFit: 'cover',
          }}
        />
      </a>
    </div>
  )
}

// Text ad component - displays text as a clickable link
const AdTextLink: React.FC<AdComponentProps> = ({ creative, onImpression, onClick }) => {
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: (inView) => {
      if (inView) onImpression()
    },
  })

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault()
    onClick()
    if (creative.click_url) {
      if (creative.click_url.startsWith('http')) {
        window.open(creative.click_url, '_blank')
      } else {
        window.location.href = creative.click_url
      }
    }
  }

  if (!creative.text_content) return null

  return (
    <div
      style={{
        textAlign: 'left',
        margin: '10px 10px 10px 0px',
      }}
      ref={ref}
    >
      <a
        href={creative.click_url || '#'}
        onClick={handleClick}
        style={{
          fontSize: 14,
          fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
          textOverflow: 'ellipsis',
          color: '#6F737E',
          display: 'block',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {creative.text_content}
      </a>
    </div>
  )
}

// HTML ad component - renders HTML content with click handling
const AdHtml: React.FC<AdComponentProps> = ({ creative, rounded, onImpression, onClick }) => {
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: (inView) => {
      if (inView) onImpression()
    },
  })

  const handleClick = (e: React.MouseEvent) => {
    // Only handle click if clicking on a link or the container itself
    const target = e.target as HTMLElement
    if (target.tagName === 'A') {
      // Let the link handle itself
      return
    }
    e.preventDefault()
    onClick()
    if (creative.click_url) {
      if (creative.click_url.startsWith('http')) {
        window.open(creative.click_url, '_blank')
      } else {
        window.location.href = creative.click_url
      }
    }
  }

  if (!creative.text_content) return null

  const style = BANNER_STYLES[creative.format]

  return (
    <div style={{ width: '100%', textAlign: 'center' }} ref={ref}>
      <div
        onClick={handleClick}
        style={{
          display: 'inline-block',
          width: style?.width,
          height: style?.height,
          overflow: 'hidden',
          cursor: creative.click_url ? 'pointer' : 'default',
          borderRadius: rounded ? 8 : undefined,
        }}
        dangerouslySetInnerHTML={{ __html: creative.text_content }}
      />
    </div>
  )
}

// Iframe ad component - embeds content in an iframe
const AdIframe: React.FC<AdComponentProps & { elementIdentifier: string }> = ({
  creative,
  rounded,
  onImpression,
  onClick,
  elementIdentifier,
}) => {
  const { ref } = useInView({
    triggerOnce: true,
    threshold: 0.8,
    onChange: (inView) => {
      if (inView) onImpression()
    },
  })

  // Listen for AD_CLICK messages from iframe
  useEffect(() => {
    function iframeListener(message: MessageEvent) {
      if (message.data?.elementIdentifier === elementIdentifier) {
        if (message.data?.type === 'AD_CLICK') {
          onClick()
          if (creative.click_url) {
            if (creative.click_url.startsWith('http')) {
              window.open(creative.click_url, '_blank')
            } else {
              window.location.href = creative.click_url
            }
          }
        }
      }
    }

    window.addEventListener('message', iframeListener)
    return () => window.removeEventListener('message', iframeListener)
  }, [creative, elementIdentifier, onClick])

  if (!creative.content_url) return null

  const style = BANNER_STYLES[creative.format]

  return (
    <div style={{ width: '100%', textAlign: 'center' }} ref={ref}>
      <iframe
        id="AdServerIframeBannerAd"
        name={elementIdentifier}
        src={creative.content_url}
        width={style?.width ? style.width + 'px' : undefined}
        style={{
          ...style,
          border: 0,
          borderRadius: rounded ? 8 : undefined,
        }}
        frameBorder={0}
        scrolling="no"
      />
    </div>
  )
}

type AdServerProps = {
  placement: string
  format: string
  rounded?: boolean
  render?: (creativeContent: ReactNode) => ReactNode
}

const AdServer: React.FC<AdServerProps> = ({ placement, format, rounded, render }) => {
  const [adData, setAdData] = useState<AdServerResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [impressionRecorded, setImpressionRecorded] = useState(false)
  const elementIdentifier = useState(() => 'ad_server_' + getRandomString())[0]
  const role = useSelector(authSelectors.getCurrentRole)
  const segmentIds = role?.segment_ids || []
  const userIdHash = useSelector((state: any) => state.auth?.user_id_hash) as string | undefined

  // Fetch ad from server
  useEffect(() => {
    const fetchAd = async () => {
      try {
        const userIdentifier = getUserIdentifier(userIdHash)

        const response = await fetch(window.API_ROOT + '/api/ads/request/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            placement: placement,
            format,
            segment_ids: segmentIds,
            user_identifier: userIdentifier,
          }),
        })

        if (response.ok) {
          const data: AdServerResponse = await response.json()
          setAdData(data)
        }
      } catch (error) {
        console.warn('Failed to fetch ad:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchAd()
  }, [placement, format])

  // Record impression when ad is visible (backend + same Amplitude/GA4 path as BannerAd.handleAdImpression)
  const recordImpression = useCallback(async () => {
    if (!adData?.campaign_id || impressionRecorded) return

    setImpressionRecorded(true)

    const creative = adData.creative
    if (creative) {
      logBannerAdImpression({
        campaign_name: adData.campaign_name || '',
        creative_name: creative.name || '',
        format: creative.format,
        placement,
      })
    }

    try {
      const userIdentifier = getUserIdentifier(userIdHash)

      await fetch(window.API_ROOT + '/api/ads/event/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([
          {
            placement: placement,
            campaign_id: adData.campaign_id,
            event_type: AD_EVENT_TYPES.IMPRESSION,
            creative_id: creative?.id || null,
            user_identifier: userIdentifier,
          },
        ]),
      })
    } catch (error) {
      console.warn('Failed to record impression:', error)
    }
  }, [adData, impressionRecorded, placement, userIdHash])

  // Record click event (backend + same Amplitude/GA4 path as BannerAd.handleBannerClick)
  const recordClick = useCallback(async () => {
    if (!adData?.campaign_id) return

    const creative = adData.creative
    if (creative) {
      logBannerAdClick({
        campaign_name: adData.campaign_name || '',
        creative_name: creative.name || '',
        format: creative.format,
        placement,
      })
    }

    try {
      const userIdentifier = getUserIdentifier(userIdHash)

      await fetch(window.API_ROOT + '/api/ads/event/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify([
          {
            placement: placement,
            campaign_id: adData.campaign_id,
            event_type: AD_EVENT_TYPES.CLICK,
            creative_id: creative?.id,
            user_identifier: userIdentifier,
          },
        ]),
      })
    } catch (error) {
      console.warn('Failed to record click:', error)
    }
  }, [adData, placement, userIdHash])

  if (loading) {
    return null
  }

  if (!adData?.creative) {
    return null
  }

  const creative = adData.creative
  const contentType = creative.content_type

  let creativeContent: ReactNode

  if (contentType === 'image') {
    creativeContent = (
      <AdBanner creative={creative} rounded={rounded} onImpression={recordImpression} onClick={recordClick} />
    )
  } else if (contentType === 'text') {
    creativeContent = <AdTextLink creative={creative} onImpression={recordImpression} onClick={recordClick} />
  } else if (contentType === 'html') {
    creativeContent = (
      <AdHtml creative={creative} rounded={rounded} onImpression={recordImpression} onClick={recordClick} />
    )
  } else if (contentType === 'iframe') {
    creativeContent = (
      <AdIframe
        creative={creative}
        rounded={rounded}
        onImpression={recordImpression}
        onClick={recordClick}
        elementIdentifier={elementIdentifier}
      />
    )
  } else if (creative.image_file_contents) {
    creativeContent = (
      <AdBanner creative={creative} rounded={rounded} onImpression={recordImpression} onClick={recordClick} />
    )
  } else if (creative.content_url) {
    creativeContent = (
      <AdIframe
        creative={creative}
        rounded={rounded}
        onImpression={recordImpression}
        onClick={recordClick}
        elementIdentifier={elementIdentifier}
      />
    )
  } else {
    creativeContent = null
  }

  if (creativeContent == null) {
    return null
  }

  return render ? render(creativeContent) : creativeContent
}

export default AdServer
